export type InsightCard = {
  n: number;
  title: string;
  body: string;
  align: 'left' | 'right';
  kind: 'macro' | 'weekly';
};

type DayStat = {
  date: string;
  totalVolume?: number;
  targetVolume?: number;
  frequencyScore?: string | number;
  volumeScore?: string | number;
  successfulReminders?: number;
  totalReminders?: number;
};

type InsightInput = {
  weeklyStats: DayStat[];
  totalIntakeToday: number;
  dailyTarget: number;
  avgVolumePerReminder: number;
  targetVolumePerReminder: number;
  volumeHits: number;
  frequencyHits: number;
  activeDaysInWindow: number;
  weeklyAverageL: number;
};

function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

/** Macro = today/session; Weekly = 7 active-day window patterns. */
export function generateInsights(input: InsightInput): InsightCard[] {
  const {
    weeklyStats,
    totalIntakeToday,
    dailyTarget,
    avgVolumePerReminder,
    targetVolumePerReminder,
    volumeHits,
    frequencyHits,
    activeDaysInWindow,
    weeklyAverageL,
  } = input;

  const volPct = dailyTarget > 0 ? (totalIntakeToday / dailyTarget) * 100 : 0;
  const avgRatio = targetVolumePerReminder > 0 ? avgVolumePerReminder / targetVolumePerReminder : 1;

  const macro: InsightCard =
    volPct < 50
      ? {
          n: 1,
          kind: 'macro',
          title: 'Volume is behind pace',
          body: `You're at ${Math.round(volPct)}% of today's ${(dailyTarget / 1000).toFixed(1)}L goal. Log a larger sip on your next reminder.`,
          align: 'left',
        }
      : avgRatio < 0.85
        ? {
            n: 1,
            kind: 'macro',
            title: 'Raise your average sip size',
            body: `You're averaging ${avgVolumePerReminder}ml per reminder vs a ${targetVolumePerReminder}ml target. One bigger log closes the gap.`,
            align: 'left',
          }
        : {
            n: 1,
            kind: 'macro',
            title: 'Strong session rhythm',
            body: `Volume and sip size are on track today. Keep responding when reminders land.`,
            align: 'left',
          };

  const volConsistency = pct(volumeHits, activeDaysInWindow);
  const freqConsistency = pct(frequencyHits, activeDaysInWindow);

  let weekly: InsightCard;
  if (volConsistency < freqConsistency - 15) {
    weekly = {
      n: 2,
      kind: 'weekly',
      title: 'Weekly volume needs attention',
      body: `Volume goals hit ${volumeHits}/${activeDaysInWindow} active days (${volConsistency}%). Frequency is ahead — focus on larger, consistent logs.`,
      align: 'right',
    };
  } else if (freqConsistency < volConsistency - 15) {
    weekly = {
      n: 2,
      kind: 'weekly',
      title: 'Answer more reminders',
      body: `Frequency goals hit ${frequencyHits}/${activeDaysInWindow} active days (${freqConsistency}%). Weekly average: ${weeklyAverageL.toFixed(1)}L.`,
      align: 'right',
    };
  } else if (weeklyStats.length >= 2) {
    const recent = weeklyStats.slice(-3);
    const older = weeklyStats.slice(0, Math.max(1, weeklyStats.length - 3));
    const recentAvg =
      recent.reduce((s, d) => s + (d.totalVolume || 0), 0) / Math.max(recent.length, 1);
    const olderAvg =
      older.reduce((s, d) => s + (d.totalVolume || 0), 0) / Math.max(older.length, 1);
    const trend = recentAvg - olderAvg;
    weekly = {
      n: 2,
      kind: 'weekly',
      title: trend >= 0 ? 'Weekly volume trending up' : 'Weekly volume slipped',
      body:
        trend >= 0
          ? `Last ${recent.length} active days average ${(recentAvg / 1000).toFixed(1)}L — up from earlier in the window.`
          : `Recent active days dipped to ${(recentAvg / 1000).toFixed(1)}L. Protect your reminder streak tomorrow.`,
      align: 'right',
    };
  } else {
    weekly = {
      n: 2,
      kind: 'weekly',
      title: 'Build your weekly baseline',
      body: `Log through ${activeDaysInWindow} active days to unlock richer weekly insights.`,
      align: 'right',
    };
  }

  return [macro, weekly];
}
